import React from 'react';

import { Autocomplete, Loader } from '@cowbell/ui';

import type { IndustryValue } from '../../../../naics/LanguageIndustryAutoComplete';
import { useIndustryCodeSearch } from '../../../../naics/useIndustryCodeSearch';
import type { Languages } from '../../../../i18n/i18n.language-config';
import {
  toNaicsOptions,
  toSicOptions,
  usesSicClassification,
} from './org-industry-search';

interface OrgIndustryFieldProps {
  readonly label: string;
  readonly defaultValue: string;
  /** The platform's language, which decides NAICS or SIC. */
  readonly language: Languages;
  readonly onSelect: (value: IndustryValue) => void;
}

/**
 * The industry code, in @cowbell/ui components, over the console's existing
 * NAICS/SIC search.
 *
 * <p>The field holds the display label ("722513 [Limited-Service
 * Restaurants]") while the code and its industry descriptions are written to
 * the form by `onSelect` -- which is why this is not a plain RHF input: the
 * selection sets several sibling fields, not one.
 *
 * <p>Which codes it searches follows the platform: SIC on the UK and
 * Australian platforms, NAICS elsewhere, as `LanguageIndustryAutoComplete`
 * did by rendering a different component for each.
 */
export const OrgIndustryField = ({
  label,
  defaultValue,
  language,
  onSelect,
}: OrgIndustryFieldProps) => {
  // The language scopes the query as well as choosing the mapper. Passing only the
  // mapper left the catalog on the platform's country, so an account whose country
  // disagreed with the platform flags got a SIC mapper over NAICS rows and an empty
  // picker.
  const search = useIndustryCodeSearch(
    usesSicClassification(language) ? toSicOptions : toNaicsOptions,
    language
  );
  const [text, setText] = React.useState(defaultValue);

  // Held in a ref so the search effect below depends only on the term. The
  // hook memoises its callback, but depending on it directly means any future
  // caller that does not gets an effect that re-runs itself forever.
  const searchRef = React.useRef(search);
  searchRef.current = search;
  const [options, setOptions] = React.useState<IndustryValue[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Keeps the field in step when the account finishes loading, or when a save
  // resets the form, without fighting the user mid-type.
  React.useEffect(() => setText(defaultValue), [defaultValue]);

  React.useEffect(() => {
    const term = text.trim();

    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }

    let live = true;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchRef
        .current(term)
        .then((found) => {
          if (live) setOptions(found);
        })
        .catch(() => {
          if (live) setOptions([]);
        })
        .finally(() => {
          if (live) setLoading(false);
        });
    }, 350);

    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [text]);

  const handleSubmit = (submitted: string) => {
    const picked = options.find((option) => option.label === submitted);
    if (picked) {
      onSelect(picked);
    }
  };

  return (
    <Autocomplete
      label={label}
      value={text}
      onChange={setText}
      onOptionSubmit={handleSubmit}
      data={options.map((option) => option.label)}
      // The search already happened server-side. Mantine filters `data`
      // against the typed text as well by default, which hides real matches
      // whose label does not contain it -- an industry code with no
      // description searched for by description, for instance.
      filter={({ options: found }) => found}
      placeholder="Type to search"
      rightSection={loading ? <Loader size="xs" /> : null}
    />
  );
};

export default OrgIndustryField;
